import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { SearchEventContainer } from "../../styles";

import z from "zod";
import { useSelector } from "react-redux";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import ControlledSelect from "../../../../components/ControlledInputs/select";
import { FormRow } from "../../../../components/ControlledInputs/styles";
import Button from "../Button";
import {
  getReportFilterEventsByYearService,
  getReportFilterYearsService,
} from "../../../../service/reportFilter.service";

const SearchEventSchema = z.object({
  year: z.object({
    value: z.coerce.number().min(1),
    label: z.string().min(1),
  }),
  eventEdition: z.object({
    value: z.coerce.number().min(1),
    label: z.string().min(1),
  }),
});

export default function SearchEvent({ setSelectedEvent, setSelectedYear }) {
  const intl = useIntl();
  const { token } = useSelector((state) => state.auth);
  const [years, setYears] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [yearSelected, setYearSelected] = useState(false);

  const form = useForm({
    resolver: zodResolver(SearchEventSchema),
    defaultValues: {
      year: "",
      eventEdition: "",
    },
  });
  const { control, handleSubmit, formState, setValue } = form;

  useEffect(() => {
    let mounted = true;

    setLoadingYears(true);
    getReportFilterYearsService({ token })
      .then((result) => {
        if (!mounted) {
          return;
        }
        setYears(
          (result?.data?.output ?? []).map((year) => ({
            label: String(year),
            value: year,
          })),
        );
      })
      .catch(() => {
        if (mounted) {
          setYears([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoadingYears(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  const onChangeYear = useCallback(
    (option) => {
      const year = option?.value ?? null;

      setValue("eventEdition", "");
      setEvents([]);
      setSelectedEvent("");
      setYearSelected(year !== null);
      setSelectedYear(year ?? "");

      if (year === null) {
        return;
      }

      setLoadingEvents(true);
      getReportFilterEventsByYearService({ token, year })
        .then((result) => {
          setEvents(
            (result?.data?.output ?? []).map((event) => ({
              label: event.eventEditionName,
              value: event.idNumber,
            })),
          );
        })
        .catch(() => setEvents([]))
        .finally(() => setLoadingEvents(false));
    },
    [setValue, setSelectedEvent, setSelectedYear, token],
  );

  const onSubmit = (data) => {
    setSelectedYear(data.year.value);
    setSelectedEvent(
      data.eventEdition.value === Infinity ? "" : data.eventEdition.value,
    );
  };

  return (
    <SearchEventContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <ControlledSelect
            name={"year"}
            label={"year"}
            required
            placeholder={intl.formatMessage({ id: "select" })}
            control={control}
            options={years}
            disabled={loadingYears}
            onChange={onChangeYear}
            errors={formState.errors}
            defaultValue={formState.defaultValues.year}
          />
          <ControlledSelect
            name={"eventEdition"}
            label={"event_edition"}
            required
            placeholder={intl.formatMessage({ id: "select" })}
            control={control}
            options={
              events.length > 0
                ? [
                    { label: intl.formatMessage({ id: "all" }), value: Infinity },
                    ...events,
                  ]
                : []
            }
            disabled={!yearSelected || loadingEvents}
            closeMenuOnSelect={false}
            errors={formState.errors}
            defaultValue={formState.defaultValues.eventEdition}
          />
          <Button
            onClick={handleSubmit(onSubmit)}
            label={"search"}
            size={"lg"}
            theme={"default"}
            Icon={"search"}
          />
        </FormRow>
      </form>
    </SearchEventContainer>
  );
}
